/**
 * Esquema Zod del formulario de **asignación de talonario**.
 *
 * Valida la entrada del modal ANTES de llamar a la API (`docs/
 * frontend_conventions.md`, Validación). El tipo del formulario se **infiere** del
 * esquema (`z.infer`), no se escribe a mano. Refleja el contrato de
 * `POST /api/v1/talonarios/assign` (`progress/explore_talonarios_api.md`, §3.A):
 * `new_start`/`new_end` enteros y el rango con exactamente 50 números.
 *
 * El formulario trabaja con los números como **texto** (lo que emite
 * `<input type="number">`); la pantalla los convierte a entero al enviar. La
 * validación de solapamiento con otros trabajadores la hace el backend
 * (`assign_failed`), no se duplica aquí.
 */
import { z } from "zod";

import { TALONARIO_SIZE } from "./constants";

/** Texto de un número de talonario: obligatorio, entero. */
const numberField = z
  .string()
  .trim()
  .min(1, "Este campo es obligatorio.")
  .regex(/^\d+$/, "Debe ser un número entero.");

/**
 * Esquema de asignación: número inicial y final, ambos enteros. La regla del
 * tamaño (50 números) se valida en el refinamiento porque cruza ambos campos.
 */
const assignBase = z.object({
  worker: z.string().trim().min(1, "El trabajador es obligatorio."),
  new_start: numberField,
  new_end: numberField,
});

export const assignTalonarioSchema = assignBase.superRefine((values, ctx) => {
  const start = Number(values.new_start);
  const end = Number(values.new_end);
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return;
  }
  if (end - start + 1 !== TALONARIO_SIZE) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["new_end"],
      message: `El talonario debe tener exactamente ${TALONARIO_SIZE} números.`,
    });
  }
});

/** Valores del formulario de asignación (inferidos del esquema). */
export type AssignTalonarioValues = z.infer<typeof assignTalonarioSchema>;
